import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '@/contexts/AuthContext';
import { useSystemBarsStyle, systemBarsIconsForBackground } from '@/hooks/useSystemBarsStyle';
import { getClientProfile } from '@/services/clientProfile';
import { getHomeCityName } from '@/utils/homeCityStorage';
import { impactLight } from '@/utils/nativeHaptics';
import { HomeLocationSheets } from '@/components/client/mobile/HomeLocationSheets';
import { getHeaderTitle } from '@/components/client/mobile/getHeaderTitle';
import { NotificationBell } from '@/components/client/notification/NotificationBell';
import {
  clientPaths,
  getEventSlugFromGamePath,
  isClientHome,
  isCommunityDetailPath,
  isEventGamePath,
  isEventStatistikPath,
  isPath,
  isPathPrefix,
  isPublicProfilePath,
} from '@/utils/clientPaths';
import { getSwipeBackFallback, historyCanGoBack } from '@/utils/nativeNavigation';
import { hostPaths } from '@/utils/hostPaths';

export const ClientMobileTopbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCommunityUser = user?.role === 'community';
  const [cityName, setCityName] = useState<string>(() => getHomeCityName());
  const [locationOpen, setLocationOpen] = useState(false);

  const pathname = location.pathname;
  const isHome = isClientHome(pathname);

  useEffect(() => {
    if (!isHome) {
      return;
    }

    setCityName(getHomeCityName());

    if (!user) {
      return;
    }

    const loadProfileCity = async () => {
      try {
        const profile = await getClientProfile();
        const profileCityName = profile?.data?.information?.location_name;
        setCityName(getHomeCityName({ profileCityName }));
      } catch {
        setCityName(getHomeCityName());
      }
    };

    void loadProfileCity();
  }, [isHome, user]);

  const { title, subtitle, isSubpage } = getHeaderTitle(pathname);
  const [dynamicSubtitle, setDynamicSubtitle] = useState<string | null>(null);

  useEffect(() => {
    setDynamicSubtitle(null);
    const handleSubtitle = (e: Event) => {
      const customEv = e as CustomEvent<{ subtitle?: string | null }>;
      if (customEv.detail && 'subtitle' in customEv.detail) {
        setDynamicSubtitle(customEv.detail.subtitle ?? null);
      }
    };

    window.addEventListener('sporto-topbar-subtitle', handleSubtitle);
    window.dispatchEvent(new Event('sporto-request-topbar-subtitle'));

    return () => {
      window.removeEventListener('sporto-topbar-subtitle', handleSubtitle);
    };
  }, [pathname]);

  const activeSubtitle = dynamicSubtitle ?? subtitle;

  const isGuest = !user;
  // User display name & avatar initial
  const displayName = user?.name || user?.username || 'Pemain';
  const initial = displayName.charAt(0).toUpperCase();
  const homeGreeting = isGuest ? 'Selamat datang' : `Halo, ${displayName}`;

  const isHasilPage = isEventStatistikPath(pathname) || isEventGamePath(pathname);
  const isWhiteHeaderPage =
    isSubpage ||
    (isPathPrefix(pathname, clientPaths.booking.list) && !isPath(pathname, clientPaths.booking.list)) ||
    isPathPrefix(pathname, clientPaths.profile.mmr) ||
    isPathPrefix(pathname, clientPaths.mmr.leaderboard) ||
    isPathPrefix(pathname, clientPaths.myCommunity) ||
    isPathPrefix(pathname, clientPaths.transactions.list);

  const isNoTopbarPage =
    isCommunityDetailPath(pathname) ||
    isPublicProfilePath(pathname) ||
    isPath(pathname, clientPaths.profile.settings) ||
    pathname.endsWith('/hasil') ||
    pathname.includes('/badminton/tambah-game') ||
    /\/badminton\/game\/[^/]+\/skor$/.test(pathname) ||
    /\/padel\/game\/[^/]+\/skor$/.test(pathname);

  const isLightStatusBarSurface = isNoTopbarPage || isHasilPage || isWhiteHeaderPage;
  useSystemBarsStyle(systemBarsIconsForBackground(isLightStatusBarSurface ? 'light' : 'dark'));

  if (isNoTopbarPage) {
    return null;
  }

  const backFallbackPath = () => {
    if (isCommunityUser) return hostPaths.home;
    return getSwipeBackFallback(pathname);
  };

  const handleHasilBackClick = () => {
    if (historyCanGoBack()) {
      navigate(-1);
      return;
    }
    if (isCommunityUser) {
      navigate(hostPaths.home);
      return;
    }
    const slug = getEventSlugFromGamePath(pathname);
    if (slug) {
      navigate(clientPaths.event.statistik(slug));
      return;
    }
    navigate(clientPaths.event.list);
  };

  if (isHasilPage) {
    return (
      <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#E0E6ED] shadow-2xs pt-safe md:hidden">
        <div className="flex items-center px-4 h-14">
          <button
            type="button"
            onClick={handleHasilBackClick}
            className="flex items-center gap-2 text-[#0E1726] font-bold text-lg hover:opacity-80 active:scale-95 transition-all cursor-pointer -ml-1 py-1 px-2 rounded-lg"
          >
            <Icon icon="ph:arrow-left" className="w-5 h-5 text-[#0E1726]" />
            <span>Hasil Event</span>
          </button>
        </div>
      </header>
    );
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 shadow-2xs pt-safe md:hidden ${
        isWhiteHeaderPage
          ? 'bg-white text-[#0E1726] border-b border-[#E0E6ED]'
          : 'bg-[#FC480D] text-white shadow-sm'
      }`}
    >
      <div className="flex items-center justify-between px-4 h-14">
        {isHome ? (
          <>
          <div className="flex items-center gap-2.5 min-w-0">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={displayName}
                className="w-[38px] h-[38px] min-w-[38px] min-h-[38px] rounded-full object-cover border-2 border-white/60 flex-shrink-0"
              />
            ) : isGuest ? (
              <div className="w-[38px] h-[38px] min-w-[38px] min-h-[38px] rounded-full bg-white/20 border-2 border-white/60 flex items-center justify-center flex-shrink-0">
                <Icon icon="ph:user" className="w-5 h-5 text-white" aria-hidden />
              </div>
            ) : (
              <div className="w-[38px] h-[38px] min-w-[38px] min-h-[38px] rounded-full bg-white/20 border-2 border-white/60 flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
                {initial}
              </div>
            )}
            <div className="flex flex-col min-w-0">
              <span className="text-[15px] font-semibold leading-snug truncate">
                {homeGreeting}
              </span>
              <button
                type="button"
                onClick={() => {
                  impactLight();
                  setLocationOpen(true);
                }}
                className="flex items-center gap-1 text-xs text-white/90 leading-tight min-h-11 -my-2 py-2 active:opacity-80"
                aria-label="Pilih kota"
              >
                <Icon icon="ph:map-pin" className="w-3.5 h-3.5 flex-shrink-0 text-white/90" />
                <span className="truncate">{cityName}</span>
                <Icon icon="ph:caret-down-bold" className="w-3 h-3 flex-shrink-0" />
              </button>
            </div>
          </div>
          <NotificationBell tone="light" />
          </>
        ) : (
          /* Non-Home Pages Topbar View */
          <>
            <div className="flex items-center gap-3 min-w-0">
              {isSubpage && (
                <button
                  type="button"
                  onClick={() => {
                    if (historyCanGoBack()) {
                      navigate(-1);
                      return;
                    }
                    navigate(backFallbackPath());
                  }}
                  className={`p-1 -ml-1 rounded-full transition-transform cursor-pointer active:scale-95 ${
                    isWhiteHeaderPage
                      ? 'text-[#0E1726] hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                  aria-label="Kembali"
                >
                  <Icon icon="ph:arrow-left" className="w-5 h-5" />
                </button>
              )}
              <div className="flex flex-col min-w-0">
                <h1
                  className={`text-base font-bold truncate leading-tight ${
                    isWhiteHeaderPage ? 'text-[#0E1726]' : 'text-white'
                  }`}
                >
                  {title}
                </h1>
                {activeSubtitle && (
                  <p className="text-[11px] font-medium text-gray-400 truncate leading-tight mt-0.5">
                    {activeSubtitle}
                  </p>
                )}
              </div>
            </div>

            {pathname.startsWith('/cash') && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('sporto-cashflow-export'))}
                className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-200/80 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
                aria-label="Export / Laporan"
              >
                <Icon icon="ph:export-bold" className="w-4.5 h-4.5" />
              </button>
            )}
          </>
        )}
      </div>
      <HomeLocationSheets
        open={locationOpen}
        currentCity={cityName}
        onClose={() => setLocationOpen(false)}
        onCityChange={setCityName}
      />
    </header>
  );
};

export default ClientMobileTopbar;
